import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faTimes } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import LeftNavBar from './LeftNavBar';
import MercenaryBar from './MercenaryBar';
import { useMercenary } from '../../contexts/MercenaryContext';
import { useSocket } from '../../contexts/SocketContext';
import { useLocation } from 'react-router-dom';
import styles from './MobileHeader.module.css';

interface SeasonInfo {
  id: number;
  name: string;
  seasonNumber: number;
  currentDayNumber: number | null;
  totalDays?: number;
  status: string;
}

interface ScheduleEvent {
  id: number;
  eventType: string;
  eventName: string;
  scheduledDate: string;
  status: string;
}

interface MobileHeaderProps {
  user: any;
}

const MobileHeader: React.FC<MobileHeaderProps> = ({ user }) => {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [isMercenaryOpen, setIsMercenaryOpen] = useState(false);
  const [isNavClosing, setIsNavClosing] = useState(false);
  const [isMercClosing, setIsMercClosing] = useState(false);
  const [regenTimerProgress, setRegenTimerProgress] = useState<number>(0);
  const [seasonInfo, setSeasonInfo] = useState<SeasonInfo | null>(null);
  const [unreadMails, setUnreadMails] = useState<number>(0);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleEvents, setScheduleEvents] = useState<ScheduleEvent[]>([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const { mercenary, setMercenary } = useMercenary();
  const { socket } = useSocket();
  const location = useLocation();

  const CLOSE_DURATION = 250; // ms, matches CSS animation

  const toggleNav = () => {
    if (isNavOpen) {
      animateCloseNav();
    } else {
      setIsNavOpen(true);
      if (isMercenaryOpen) animateCloseMerc();
    }
  };

  const toggleMercenary = () => {
    if (isMercenaryOpen) {
      animateCloseMerc();
    } else {
      setIsMercenaryOpen(true);
      if (isNavOpen) animateCloseNav();
    }
  };

  const animateCloseNav = useCallback(() => {
    if (isNavClosing) return;
    setIsNavClosing(true);
    setTimeout(() => { setIsNavOpen(false); setIsNavClosing(false); }, CLOSE_DURATION);
  }, [isNavClosing]);

  const animateCloseMerc = useCallback(() => {
    if (isMercClosing) return;
    setIsMercClosing(true);
    setTimeout(() => { setIsMercenaryOpen(false); setIsMercClosing(false); }, CLOSE_DURATION);
  }, [isMercClosing]);

  const closeMenus = useCallback(() => {
    if (isNavOpen) animateCloseNav();
    if (isMercenaryOpen) animateCloseMerc();
  }, [isNavOpen, isMercenaryOpen, animateCloseNav, animateCloseMerc]);

  // Close menus when navigation changes (user clicks a link), instant, no animation
  useEffect(() => {
    setIsNavOpen(false); setIsNavClosing(false);
    setIsMercenaryOpen(false); setIsMercClosing(false);
  }, [location.pathname]);

  // Close menus on Escape key
  useEffect(() => {
    if (!isNavOpen && !isMercenaryOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMenus();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isNavOpen, isMercenaryOpen]);

  // Swipe-to-close for drawers
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  }, []);

  const handleNavSwipeEnd = useCallback((e: React.TouchEvent) => {
    if (!touchStartRef.current) return;
    const dx = e.changedTouches[0].clientX - touchStartRef.current.x;
    const dy = Math.abs(e.changedTouches[0].clientY - touchStartRef.current.y);
    // Swipe left to close nav (left drawer)
    if (dx < -60 && dy < 100) closeMenus();
    touchStartRef.current = null;
  }, [closeMenus]);

  const handleMercSwipeEnd = useCallback((e: React.TouchEvent) => {
    if (!touchStartRef.current) return;
    const dx = e.changedTouches[0].clientX - touchStartRef.current.x;
    const dy = Math.abs(e.changedTouches[0].clientY - touchStartRef.current.y);
    // Swipe right to close mercenary (right drawer)
    if (dx > 60 && dy < 100) closeMenus();
    touchStartRef.current = null;
  }, [closeMenus]);

  // Listen to socket events for real-time updates
  useEffect(() => {
    if (!socket || !mercenary) return;

    const handleMercenaryRegen = (data: { mercenaryId: number; regen: { hp: number; rounds: number; maxHp?: number; maxRounds?: number; restedExp?: number } }) => {
      const { mercenaryId, regen } = data;
      const activeMercenaryId = parseInt(localStorage.getItem('mercenaryId') || '', 10);
      
      if (mercenaryId === activeMercenaryId) {
        setMercenary((prevMercenary: any) => {
          if (!prevMercenary) return prevMercenary;
          
          const updates: any = {
            health: regen.hp,
            effectiveHealth: regen.hp,
            rounds: regen.rounds,
            restedExp: regen.restedExp,
          };
          
          if (regen.maxHp !== undefined) {
            updates.maxHealth = regen.maxHp;
            updates.effectiveMaxHealth = regen.maxHp;
          }
          if (regen.maxRounds !== undefined) updates.maxRounds = regen.maxRounds;
          
          return { ...prevMercenary, ...updates };
        });
      }
    };

    const handleMercenaryUpdate = (data: { mercenaryId: number; updatedStats: any }) => {
      const { mercenaryId, updatedStats } = data;
      const activeMercenaryId = parseInt(localStorage.getItem('mercenaryId') || '', 10);
      
      if (mercenaryId === activeMercenaryId) {
        setMercenary((prevMercenary: any) => {
          if (!prevMercenary) return prevMercenary;
          return { ...prevMercenary, ...updatedStats };
        });
      }
    };

    socket.on('mercenary_regen', handleMercenaryRegen);
    socket.on('update_mercenary_stats', handleMercenaryUpdate);

    return () => {
      socket.off('mercenary_regen', handleMercenaryRegen);
      socket.off('update_mercenary_stats', handleMercenaryUpdate);
    };
  }, [socket, mercenary?.id, setMercenary]);

  // Track unread mail count for the notification dot on the portrait button
  useEffect(() => {
    if (!socket) return;
    const handleNewMail = () => setUnreadMails(prev => prev + 1);
    const handleMailRead = () => setUnreadMails(prev => Math.max(prev - 1, 0));
    socket.on('newMail', handleNewMail);
    socket.on('mailRead', handleMailRead);
    return () => {
      socket.off('newMail', handleNewMail);
      socket.off('mailRead', handleMailRead);
    };
  }, [socket]);

  // Fetch initial unread mail count on mount
  useEffect(() => {
    if (!user) return;
    axios.get('/api/mail/unread-count', { withCredentials: true })
      .then(res => setUnreadMails(res.data.count ?? 0))
      .catch(() => {});
  }, [user]);

  // Regen timer effect (same as MercenaryBar)
  useEffect(() => {
    const interval = setInterval(() => {
      setRegenTimerProgress(Date.now() % 180000);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Fetch season info
  useEffect(() => {
    const fetchSeasonInfo = async () => {
      try {
        const response = await axios.get('/api/user/current-season', { withCredentials: true });
        if (response.data.season) {
          setSeasonInfo(response.data.season);
        }
      } catch (error) {
        console.error('Error fetching season info:', error);
      }
    };

    if (user) {
      fetchSeasonInfo();
      const interval = setInterval(fetchSeasonInfo, 300000);

      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') fetchSeasonInfo();
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        clearInterval(interval);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, [user]);

  const handleSeasonBadgeClick = async () => {
    if (!seasonInfo) return;
    setShowScheduleModal(true);
    setScheduleLoading(true);
    try {
      const res = await axios.get(`/api/seasons/${seasonInfo.id}/schedule`, { withCredentials: true });
      const upcoming = (res.data.events || []).filter(
        (e: ScheduleEvent) => e.status === 'scheduled' || e.status === 'in_progress'
      );
      setScheduleEvents(upcoming);
    } catch {
      setScheduleEvents([]);
    } finally {
      setScheduleLoading(false);
    }
  };

  if (!user) return null;

  const currentHealth = mercenary?.effectiveHealth ?? mercenary?.health ?? 0;
  const maxHealth = mercenary?.effectiveMaxHealth ?? mercenary?.maxHealth ?? 1;

  return (
    <>
      <div className={styles.mobileHeader}>
        {/* Hamburger Menu Button */}
        <button 
          className={styles.hamburgerButton}
          onClick={toggleNav}
          aria-label="Toggle navigation menu"
        >
          <FontAwesomeIcon icon={isNavOpen ? faTimes : faBars} />
        </button>

        {/* Game Title/Logo with Season Info */}
        <div className={styles.gameTitle}>
          <span className={styles.titleText}>NJORDHEIM</span>
          {seasonInfo && (
            <button
              className={styles.seasonBadge}
              onClick={handleSeasonBadgeClick}
              aria-label="View season schedule"
            >
              Day {(seasonInfo.currentDayNumber ?? -1) + 1}/{seasonInfo.totalDays ?? 53}
            </button>
          )}
        </div>

        {/* Mercenary Stats Summary */}
        {mercenary && (
          <div className={styles.mercenaryStats}>
            <div className={styles.statItem}>
              <span className={styles.statLabel}>HP</span>
              <span className={styles.statValue}>{Math.floor(currentHealth)}/{Math.floor(maxHealth)}</span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statLabel}>XP</span>
              <span className={styles.statValue}>
                {((mercenary.experience || 0) - (mercenary.previousExperience || 0))}/
                {((mercenary.nextExperience || 0) - (mercenary.previousExperience || 0))}
              </span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statLabel}>Rounds</span>
              <span className={styles.statValue}>
                {Math.floor(mercenary.rounds || 0)}/{mercenary.maxRounds || 0}
              </span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statLabel}>Regen</span>
              <span className={styles.statValue}>
                {Math.floor((180000 - regenTimerProgress) / 60000)}:
                {Math.floor(((180000 - regenTimerProgress) % 60000) / 1000).toString().padStart(2, '0')}
              </span>
            </div>
          </div>
        )}

        {/* Mercenary Portrait */}
        {mercenary && (
          <button 
            className={styles.mercenaryPortrait}
            onClick={toggleMercenary}
            aria-label="Toggle mercenary menu"
          >
            <img
              src={`/img/avatars/${mercenary.race?.toLowerCase() || 'default'}.${mercenary.sex?.toLowerCase() || 'default'}.${mercenary.avatar || 1}.png`}
              alt={mercenary.name || 'Mercenary'}
              className={styles.portraitImage}
            />
            {unreadMails > 0 && (
              <span className={styles.portraitNotificationDot} title={`${unreadMails} unread mail`} />
            )}
          </button>
        )}
      </div>

      {/* Mobile Navigation Overlay */}
      {isNavOpen && (
        <div className={`${styles.mobileOverlay} ${isNavClosing ? styles.overlayClosing : ''}`} onClick={closeMenus}>
          <div
            className={`${styles.mobileNavContainer} ${isNavClosing ? styles.navClosing : ''}`}
            onClick={(e) => e.stopPropagation()}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleNavSwipeEnd}
          >
            <div className={styles.drawerCloseBar}>
              <button className={styles.drawerCloseButton} onClick={closeMenus} aria-label="Close menu">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <LeftNavBar />
          </div>
        </div>
      )}

      {/* Mobile Mercenary Overlay */}
      {isMercenaryOpen && (
        <div className={`${styles.mobileOverlay} ${isMercClosing ? styles.overlayClosing : ''}`} onClick={closeMenus}>
          <div
            className={`${styles.mobileMercenaryContainer} ${isMercClosing ? styles.mercClosing : ''}`}
            onClick={(e) => e.stopPropagation()}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleMercSwipeEnd}
          >
            <div className={`${styles.drawerCloseBar} ${styles.drawerCloseBarLeft}`}>
              <button className={styles.drawerCloseButton} onClick={closeMenus} aria-label="Close mercenary panel">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <MercenaryBar />
          </div>
        </div>
      )}

      {/* Season Schedule Modal */}
      {showScheduleModal && (
        <div className={styles.scheduleOverlay} onClick={() => setShowScheduleModal(false)}>
          <div className={styles.scheduleSheet} onClick={(e) => e.stopPropagation()}>
            <div className={styles.scheduleSheetHandle} />
            <div className={styles.scheduleSheetHeader}>
              <h3 className={styles.scheduleSheetTitle}>
                {seasonInfo?.name ?? 'Season'} — Schedule
              </h3>
              <button className={styles.scheduleSheetClose} onClick={() => setShowScheduleModal(false)} aria-label="Close">✕</button>
            </div>
            <div className={styles.scheduleSheetBody}>
              {scheduleLoading ? (
                <p className={styles.scheduleMessage}>Loading…</p>
              ) : scheduleEvents.length === 0 ? (
                <p className={styles.scheduleMessage}>No upcoming events.</p>
              ) : (
                scheduleEvents.map((ev) => (
                  <div key={ev.id} className={`${styles.scheduleRow} ${ev.status === 'in_progress' ? styles.scheduleRowActive : ''}`}>
                    <div className={styles.scheduleEventName}>{ev.eventName}</div>
                    <div className={styles.scheduleEventMeta}>
                      <span className={styles.scheduleDate}>
                        {new Date(ev.scheduledDate).toLocaleString('sv-SE', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                      <span className={`${styles.scheduleBadge} ${ev.status === 'in_progress' ? styles.badgeActive : styles.badgeScheduled}`}>
                        {ev.status === 'in_progress' ? 'IN PROGRESS' : 'UPCOMING'}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MobileHeader;
